import { useEffect, useState } from "react";
import { PageHeader, Panel, Pill } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/api";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Gear, ShieldCheck, Globe, Translate as TranslateIcon, EnvelopeSimple, CheckCircle, WarningCircle, PaperPlaneTilt, Pulse } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ROLES = ["super_admin", "hq_admin", "branch_admin", "teacher", "parent", "counsellor", "transport", "support", "inventory"];

export default function Settings() {
  const { user } = useAuth();
  const isSuperish = ["super_admin", "hq_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Settings" title="Workspace & permissions" subtitle="Roles, language, currency and brand control." />

      {isSuper && (
        <Link to="/app/system-status"
          data-testid="settings-system-status-link"
          className="block rounded-2xl border border-border bg-gradient-to-r from-sage-soft/60 to-mustard-soft/40 px-4 py-3 mb-5 hover:border-sage transition group">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-white border border-sage/30 flex items-center justify-center text-sage-deep">
              <Pulse weight="duotone" size={20} />
            </span>
            <div className="flex-1">
              <div className="text-sm font-semibold text-ink flex items-center gap-2">
                System status
                <Pill tone="ink">Super-admin only</Pill>
              </div>
              <div className="text-xs text-ink-soft">Env mode · demo footprint · provider state · production readiness verdict.</div>
            </div>
            <span className="text-ink-soft group-hover:text-terracotta transition">→</span>
          </div>
        </Link>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Your profile">
          <div className="space-y-2 text-sm">
            <Row label="Name" value={user.name} />
            <Row label="Email" value={user.email} />
            <Row label="Role" value={ROLE_LABEL[user.role] || user.role} />
            <Row label="Branch" value={user.branch_id || "—"} />
          </div>
        </Panel>

        <Panel title="Workspace defaults">
          <div className="space-y-3 text-sm">
            <Row label="Default language" value="English (India)" icon={TranslateIcon} />
            <Row label="Currency" value="INR" icon={Globe} />
            <Row label="Timezone" value="Asia/Kolkata" icon={Globe} />
            <Row label="Data privacy" value="Child-safety first · India DPDP-ready" icon={ShieldCheck} />
          </div>
        </Panel>

        {isSuperish && <EmailDeliveryCard />}

        <Panel title="Role matrix" className="lg:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {ROLES.map(r => (
              <div key={r} className="p-3 rounded-2xl border border-border">
                <div className="font-semibold text-sm">{ROLE_LABEL[r] || r}</div>
                <div className="text-xs text-ink-soft mt-1">Role-scoped data access</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-2xl bg-paper-alt">
      <div className="flex items-center gap-2 text-ink-soft">{Icon && <Icon weight="duotone" />}{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

/**
 * EmailDeliveryCard — super_admin / hq_admin can see which email provider
 * is active (Brevo / Resend / mock fallback) and fire a test email
 * straight into their own inbox to confirm DNS records are doing their
 * job.  Switching providers is a one-line env-var change on the server,
 * no code redeploy needed.
 */
function EmailDeliveryCard() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/platform-settings/email/status")
      .then(({ data }) => setStatus(data))
      .catch(() => {});
  }, []);

  const send = async () => {
    if (!to.includes("@")) { toast.error("Enter a valid email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/platform-settings/email/test", { to });
      setLastResult(data);
      setStatus(data);
      if (data.provider === "brevo") toast.success("Sent via Brevo — check the inbox!");
      else if (data.provider === "resend") toast.success("Sent via Resend — check the inbox!");
      else toast.warning("Sent (mock fallback) — no provider key configured yet.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const anyConfigured = status?.configured?.brevo || status?.configured?.resend;

  return (
    <Panel title="Email delivery" testid="email-delivery-card">
      <p className="text-sm text-ink-soft mb-3">
        Active provider: <code className="font-semibold">{status?.active_provider || "…"}</code>
        {status?.preferred_env && <> · preferred: <code>{status.preferred_env}</code></>}
        {!anyConfigured && <> · no API key yet — every send falls back to the mock outbox.</>}
      </p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <ProviderChip name="Brevo" active={status?.active_provider === "brevo"}
          configured={status?.configured?.brevo}
          hint="Recommended for Wix DNS · 300/day free · email + SMS + WhatsApp" />
        <ProviderChip name="Resend" active={status?.active_provider === "resend"}
          configured={status?.configured?.resend}
          hint="3,000/month free · needs Cloudflare DNS for full setup" />
      </div>

      <div className="flex gap-2 mb-3">
        <input value={to} onChange={(e) => setTo(e.target.value)}
          type="email" placeholder="your@email.com"
          data-testid="email-test-to"
          className="flex-1 h-11 px-3 rounded-xl border border-border text-sm" />
        <button onClick={send} disabled={busy || !to}
          data-testid="email-test-send"
          className="px-4 h-11 rounded-full bg-terracotta text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2 hover:bg-terracotta-hover">
          <PaperPlaneTilt weight="bold" /> {busy ? "Sending…" : "Send test"}
        </button>
      </div>

      {lastResult && (
        <div className="rounded-2xl border border-border p-3 text-sm" data-testid="email-test-result">
          <div className="flex items-center gap-2 mb-1.5">
            {lastResult.provider !== "mock" ? (
              <><CheckCircle weight="fill" className="text-sage-deep" /> <span className="font-semibold">Sent via {lastResult.provider}</span></>
            ) : (
              <><WarningCircle weight="fill" className="text-mustard-deep" /> <span className="font-semibold">Mock fallback</span></>
            )}
          </div>
          <div className="text-xs text-ink-soft space-y-0.5">
            <div>From: <code>{lastResult.mail_from}</code></div>
            <div>Provider: <code>{lastResult.provider}</code></div>
            {lastResult.provider_id && <div>Provider id: <code>{lastResult.provider_id}</code></div>}
          </div>
        </div>
      )}

      {!anyConfigured && (
        <div className="mt-3 rounded-2xl bg-mustard-soft border border-mustard/30 p-3 text-xs space-y-3"
          data-testid="email-test-howto">
          <div>
            <div className="font-semibold flex items-center gap-1.5 mb-1">
              <EnvelopeSimple weight="duotone" /> Brevo (recommended for Wix domains)
            </div>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Sign up at <a href="https://brevo.com" target="_blank" rel="noreferrer" className="underline">brevo.com</a>, add domain <code>mangocubs.com</code>.</li>
              <li>Brevo shows TXT + DKIM records — paste them into Wix → Domains → Advanced → DNS Records (CNAMEs/TXTs work, no MX needed).</li>
              <li>Settings → SMTP and API → API keys → Create new key.</li>
              <li>Put it in <code>BREVO_API_KEY</code> on production, set <code>MAIL_PROVIDER=brevo</code>, redeploy.</li>
            </ol>
          </div>
          <div className="opacity-70">
            <div className="font-semibold mb-1">Alternative: Resend</div>
            <div>Needs MX on a subdomain → won't work on Wix's basic DNS plan. Use only if you move DNS to Cloudflare.</div>
          </div>
        </div>
      )}
    </Panel>
  );
}

function ProviderChip({ name, active, configured, hint }) {
  return (
    <div className={`rounded-2xl border p-2.5 text-xs ${
      active ? "border-terracotta bg-terracotta-soft" : configured ? "border-sage bg-sage-soft/40" : "border-border bg-white"
    }`} data-testid={`provider-chip-${name.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 font-semibold">
        {configured
          ? <CheckCircle weight="fill" size={12} className="text-sage-deep" />
          : <WarningCircle weight="fill" size={12} className="text-mustard-deep" />}
        {name}
        {active && <span className="ml-auto text-[10px] uppercase tracking-wider text-terracotta">active</span>}
      </div>
      <div className="text-ink-soft mt-1">{hint}</div>
    </div>
  );
}
